/**
 * RBACContext - Role-Based Access Control Context
 *
 * Provides RBAC state and permission checking throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { clearSiteWriteAccessCache } from '../hooks/useSiteWriteAccess';
import { apiService } from '../services/ApiService';
import RBACService, {
  Role,
  UserRole,
  Permission,
  RBACSummary,
  UserSites
} from '../services/RBACService';
import { useAuth } from './AuthContext';
import { logger } from '../utils/log';

interface RBACContextType {
  // State
  roles: UserRole[];
  permissions: string[];
  isAdmin: boolean;
  /**
   * Sites this person administers — where they hold site_admin. The server computes it
   * (RBACService.getUserRBACSummary), so the screen and the gate cannot disagree.
   *
   * It replaces three things that never worked: `isAccountAdmin`, a site-agnostic boolean
   * for a role that has been per-site since migration 020 and no longer exists at all since
   * 068; and `isSiteAdmin`/`isDataCreator`, which were only ever populated by
   * setCurrentSite — a method nothing in the app called, so they were permanently false and
   * every gate reading them was dead.
   */
  adminSiteIds: string[];
  /** May this person reach the Users & Groups screen at all? */
  canAdministerPeople: boolean;
  /**
   * May this person create sites (the Sites page "Create Site" button)? Admins, plus the
   * people in site_creators (migration 072) — which is not a role and never shows on the
   * Users & Groups screen. The server gates POST /api/assets on the same answer.
   */
  canCreateSites: boolean;
  /** Does this person administer THIS site? */
  administersSite: (siteId: string) => boolean;
  loading: boolean;
  error: string | null;

  /**
   * "VIEW AS" — what an admin is currently looking through, or null for themselves.
   *
   * It is not a pretence in the browser: the server answers reads as the simulated identity
   * (middleware/viewAs.js), so `roles`, `isAdmin`, `adminSiteIds` and every list on screen
   * are that person's real answers, fetched the way they would fetch them. Writes still run
   * as the real admin, deliberately.
   */
  viewAs: ViewAs | null;
  /** Admin-only; anyone else calling it changes nothing, because the server ignores it. */
  setViewAs: (next: ViewAs | null) => void;
  /** The real identity behind a simulation — for the banner, and for the exit. */
  realIsAdmin: boolean;

  // Available roles for admin UI
  availableRoles: Role[];

  // Methods
  /** Sites the SERVER says this user can reach. null = not known yet (never block on it). */
  accessibleSiteCount: number | null;
  hasPermission: (permissionId: string, siteId?: string) => boolean;
  hasRole: (roleId: string, siteId?: string) => boolean;

  // Refresh data
  refreshRoles: () => Promise<void>;
  refreshAvailableRoles: () => Promise<void>;

  // Admin functions
  getUserRoles: (userId: string) => Promise<UserRole[]>;
  assignRole: (userId: string, roleId: string, siteId?: string) => Promise<void>;
  revokeRole: (userId: string, roleId: string, siteId?: string) => Promise<void>;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

interface RBACProviderProps {
  children: ReactNode;
  /**
   * Embed mode: skip ALL role/permission fetches and present a static
   * read-only permission set (no roles, no admin, no site permissions).
   * The server enforces read-only for embed tokens regardless — this just
   * keeps the embed shell from firing doomed /api/rbac calls.
   */
  staticReadOnly?: boolean;
}

/** Either a role held on one site, or a specific person. */
export type ViewAs =
  | { mode: 'role'; roleId: string; siteId: string | null; label: string }
  | { mode: 'user'; userId: string; label: string };

export function RBACProvider({ children, staticReadOnly = false }: RBACProviderProps) {
  const { user, isAuthenticated } = useAuth();

  // Core state
  const [roles, setRoles] = useState<UserRole[]>([]);
  // How many sites the SERVER says this user can reach. null = not asked yet.
  //
  // The no-access screen used to be decided by `roles.length === 0`, i.e. by counting
  // user_site_roles rows. That is only ONE of the six access paths: a user whose access
  // comes from a group grant, an asset group, an onboarding assignment or having created
  // the site has no role rows at all, and was shown "you haven't been assigned to any
  // sites" while the server was happily granting them sites. /rbac/my-sites answers the
  // question the screen is actually asking, using the same predicate the 59 gated routes
  // use, so the UI and the server can no longer disagree.
  const [accessibleSiteCount, setAccessibleSiteCount] = useState<number | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminSiteIds, setAdminSiteIds] = useState<string[]>([]);
  const [canCreateSites, setCanCreateSites] = useState(false);
  const [viewAs, setViewAsState] = useState<ViewAs | null>(null);
  // Who you really are, kept across a simulation so the banner and the exit survive
  // `isAdmin` going false while you look through a Viewer's eyes.
  const [realIsAdmin, setRealIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  // Available roles for admin UI
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

  // Load user's RBAC data
  const refreshRoles = useCallback(async () => {
    // Whose access we are about to (re)read has changed, so the per-site write answers
    // cached for the previous state must not survive — a sign-out and a sign-in in the same
    // tab would otherwise show the new person the old person's buttons.
    clearSiteWriteAccessCache();
    if (!isAuthenticated) {
      setRoles([]);
      setPermissions([]);
      setIsAdmin(false);
      setAdminSiteIds([]);
      setCanCreateSites(false);
      setAccessibleSiteCount(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const summary = await RBACService.getMySummary();

      setRoles(summary.roles);
      setPermissions(summary.permissions);
      setIsAdmin(summary.isAdmin);
      setAdminSiteIds(summary.adminSiteIds || []);
      setCanCreateSites(summary.isAdmin === true || summary.canCreateSites === true);
      // Only a request made as YOURSELF can tell you whether you are an admin; while a
      // simulation is on, the answer is the simulated one, so keep the real one from before.
      if (!viewAsRef.current) setRealIsAdmin(summary.isAdmin);

      // Asked alongside the summary, never gating it: a failure here must not strand a
      // user on a loading screen, so it falls back to "unknown" and the caller treats
      // unknown as "do not block".
      RBACService.getMySites()
        .then((s) => setAccessibleSiteCount(s.allSites ? Number.MAX_SAFE_INTEGER : s.sites.length))
        .catch(() => setAccessibleSiteCount(null));

    } catch (err: any) {
      console.error('Failed to load RBAC data:', err);
      setError(err.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load available roles (for admin UI)
  const refreshAvailableRoles = useCallback(async () => {
    try {
      const roles = await RBACService.getRoles();
      setAvailableRoles(roles);
    } catch (err: any) {
      console.error('Failed to load available roles:', err);
    }
  }, []);



  // Check if user has a specific permission
  const hasPermission = useCallback((permissionId: string, siteId?: string): boolean => {
    // Admin has all permissions
    if (isAdmin) return true;

    // Check in permissions array
    return permissions.includes(permissionId);
  }, [isAdmin, permissions]);

  // Check if user has a specific role
  const hasRole = useCallback((roleId: string, siteId?: string): boolean => {
    return roles.some(r => {
      if (r.role_id !== roleId) return false;
      if (siteId && r.site_id && r.site_id !== siteId) return false;
      return true;
    });
  }, [roles]);




  // Read at request time by ApiService, so a switch applies to the very next call.
  const viewAsRef = useRef<ViewAs | null>(null);
  useEffect(() => {
    apiService.setViewAsGetter(() => {
      const v = viewAsRef.current;
      if (!v) return null;
      return v.mode === 'user'
        ? { header: `user:${v.userId}` }
        : { header: `role:${v.roleId}`, siteId: v.siteId };
    });
  }, []);

  const setViewAs = useCallback((next: ViewAs | null) => {
    viewAsRef.current = next;
    setViewAsState(next);
    try {
      if (next) sessionStorage.setItem('mabat3d_view_as', JSON.stringify(next));
      else sessionStorage.removeItem('mabat3d_view_as');
    } catch { /* private browsing — the mode simply does not survive a refresh */ }
    // Every cached "can this person write here" answer belongs to the previous identity.
    clearSiteWriteAccessCache();
    void refreshRoles();
  }, [refreshRoles]);

  // Survive a refresh: an admin who reloads mid-look should still be looking.
  useEffect(() => {
    if (!isAuthenticated || viewAsRef.current) return;
    try {
      const saved = sessionStorage.getItem('mabat3d_view_as');
      if (saved) setViewAs(JSON.parse(saved) as ViewAs);
    } catch { /* ignore a malformed value */ }
  }, [isAuthenticated, setViewAs]);

  /** Admins administer every site; a site admin administers the ones they hold. */
  const administersSite = useCallback(
    (siteId: string): boolean => isAdmin || adminSiteIds.includes(siteId),
    [isAdmin, adminSiteIds]
  );
  const canAdministerPeople = isAdmin || adminSiteIds.length > 0;

  // Admin functions
  const getUserRoles = useCallback(async (userId: string): Promise<UserRole[]> => {
    return RBACService.getUserRoles(userId);
  }, []);

  const assignRole = useCallback(async (userId: string, roleId: string, siteId?: string): Promise<void> => {
    await RBACService.assignRole(userId, roleId, siteId);
  }, []);

  const revokeRole = useCallback(async (userId: string, roleId: string, siteId?: string): Promise<void> => {
    await RBACService.revokeRole(userId, roleId, siteId);
  }, []);


  // Initial load when authenticated
  useEffect(() => {
    if (staticReadOnly) {
      setLoading(false);
      return;
    }
    if (isAuthenticated) {
      refreshRoles();
      refreshAvailableRoles();
    } else {
      setRoles([]);
      setPermissions([]);
      setIsAdmin(false);
      setAdminSiteIds([]);
      setCanCreateSites(false);
      setAvailableRoles([]);
      setLoading(false);
    }
  }, [isAuthenticated, staticReadOnly, refreshRoles, refreshAvailableRoles]);

  const value: RBACContextType = {
    roles,
    accessibleSiteCount,
    permissions,
    isAdmin,
    adminSiteIds,
    viewAs,
    setViewAs,
    realIsAdmin,
    canAdministerPeople,
    canCreateSites,
    administersSite,
    loading,
    error,
    availableRoles,
    hasPermission,
    hasRole,
    refreshRoles,
    refreshAvailableRoles,
    getUserRoles,
    assignRole,
    revokeRole
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

export function useRBAC(): RBACContextType {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within a RBACProvider');
  }
  return context;
}

export default RBACContext;
